import type { Dot, HillChart, Snapshot } from '@hillchart/shared';

/**
 * Reconstruct a `HillChart` shape from a `Snapshot` so the existing
 * `<Hill />` renderer can display a frozen past state without branching
 * on a separate "snapshot view" component.
 *
 * Rationale (CIR-009 Phase E):
 * - A snapshot stores a minimal `SnapshotDot` (externalScopeId, name, x,
 *   color, completed). A live `Dot` has extra fields — `id`, `hillChartId`,
 *   `externalScopeUrl`, `lastMovedAt`, `lastMovedByUserId` - that `<Hill />`
 *   reads for keys, ARIA, and hover affordances. To avoid forking the
 *   renderer, we fabricate the missing fields deterministically.
 * - React keys must be stable and unique *within the rendered list*.
 *   `${snapshotId}:${externalScopeId}` is deterministic (same snapshot →
 *   same key, so no remount thrash when re-rendering) and unique (one
 *   milestone per snapshot by backend invariant).
 * - `lastMovedAt` / `lastMovedByUserId` default to the snapshot's capture
 *   metadata. That's a plausible fiction: from the user's point of view,
 *   a snapshot is "this is what the hill looked like at that moment", so
 *   attributing the displayed position to the person who captured it is
 *   the most intuitive surface — even though a real replayed dot could
 *   have been moved by multiple people between snapshots.
 * - The wrapping `HillChart` inherits the live chart's project metadata
 *   (name, externalProjectId, etc.). We're viewing the same project, just
 *   at a different point in time.
 *
 * This helper is pure and side-effect-free, hence trivially testable on
 * its own (see `snapshot-to-hill-view.test.ts`).
 */
export function snapshotToHillView(snapshot: Snapshot, live: HillChart): HillChart {
  const dots: Dot[] = snapshot.dots.map((sd) => ({
    // Synthetic id scoped to the snapshot — never collides with a live
    // dot id because live ids are uuids and this one is a colon-joined
    // composite. Callers that key off the id still get stability.
    id: `${snapshot.id}:${sd.externalScopeId}`,
    hillChartId: live.id,
    externalScopeId: sd.externalScopeId,
    externalScopeName: sd.externalScopeName,
    // Snapshots don't store the upstream URL (state log, not a nav
    // surface). `<Hill />` treats a null URL as "no external link", so
    // this is the right neutral value.
    externalScopeUrl: null,
    x: sd.x,
    color: sd.color,
    completed: sd.completed,
    lastMovedByUserId: snapshot.capturedByUserId,
    lastMovedAt: snapshot.capturedAt,
    // Pass through the frozen displayOrder. This is the field that makes
    // the legend numbers identical across the live view and every
    // historical snapshot — what the user actually sees on the chart.
    displayOrder: sd.displayOrder,
  }));

  return {
    ...live,
    dots,
  };
}
